import { useCallback, useEffect, useRef, useState } from 'react'
import { useDocumentPdfFile } from '~/entities/document'
import type { AgentTranscript } from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import {
  IconButton,
  PDFDocument,
  type PDFPaginationInfo,
  Segment,
  SegmentedButton,
} from '~/shared/ui'
import { type DocumentPaneTab, useDocumentPane } from '../../model'
import { MaterialIcon, WithTip } from '../primitives'
import { TextView } from './text-view'

const TABS: Array<{ key: DocumentPaneTab; label: string; icon: string }> = [
  { key: 'pdf', label: 'PDF', icon: 'picture_as_pdf' },
  { key: 'text', label: 'Text', icon: 'notes' },
]

function PdfView({ documentKey, isActive }: { documentKey: string; isActive: boolean }) {
  const { file, isLoading } = useDocumentPdfFile(documentKey)
  const { target } = useDocumentPane()
  const scrollableRef = useRef<HTMLDivElement | null>(null)
  const onPaginationReady = useCallback((info: PDFPaginationInfo) => {
    scrollableRef.current = info.scrollableRef.current
  }, [])

  // A page asked for from the review, once it has rendered; retried until the PDF has loaded.
  const handledTarget = useRef<number | null>(null)
  useEffect(() => {
    if (!isActive || target?.kind !== 'page' || handledTarget.current === target.id) return
    let attempts = 0
    const timer = setInterval(() => {
      const page = scrollableRef.current?.querySelector(`[data-page-number="${target.page}"]`)
      if (page) {
        handledTarget.current = target.id
        page.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
      if (page || ++attempts > 40) clearInterval(timer)
    }, 150)
    return () => clearInterval(timer)
  }, [target, isActive])

  return (
    <PDFDocument
      file={file}
      isLoading={isLoading}
      initialFitToWidth
      onPaginationReady={onPaginationReady}
      onOpenInNewTab={() =>
        window.open(`/documents/${encodeURIComponent(documentKey)}/view-pdf`, '_blank')
      }
    />
  )
}

/**
 * The document beside the review, as the PDF or as the text the agent read. Evidence quotes and
 * page links in the review open here, so a claim can be checked against the source in place.
 */
export function DocumentPane({
  documentKey,
  transcripts,
}: {
  documentKey: string
  transcripts: Array<AgentTranscript | null>
}) {
  const { tab, setTab, isOpen, setIsOpen } = useDocumentPane()
  // The text view mounts on first use and then stays, keeping its scroll and find state.
  const [hasOpenedText, setHasOpenedText] = useState(tab === 'text')
  useEffect(() => {
    if (tab === 'text') setHasOpenedText(true)
  }, [tab])

  if (!isOpen) {
    return (
      <aside className="border-divider bg-surface-lowest flex w-12 shrink-0 flex-col items-center gap-2 border-l py-3">
        <WithTip tip="Show the document">
          <span>
            <IconButton
              materialIcon="left_panel_open"
              size="medium"
              container="medium"
              onClick={() => setIsOpen(true)}
            />
          </span>
        </WithTip>
        {TABS.map(({ key, label, icon }) => (
          <WithTip
            key={key}
            tip={`Show the ${label}`}
          >
            <button
              type="button"
              onClick={() => {
                setTab(key)
                setIsOpen(true)
              }}
              className="text-text-secondary hover:bg-surface-low flex size-9 cursor-pointer items-center justify-center rounded-full border-none bg-transparent"
              aria-label={`Show the ${label}`}
            >
              <MaterialIcon
                name={icon}
                className="text-[20px]"
              />
            </button>
          </WithTip>
        ))}
      </aside>
    )
  }

  return (
    <aside className="border-divider bg-surface-lowest flex w-[44%] max-w-[820px] min-w-[440px] shrink-0 flex-col border-l">
      <div className="border-divider flex h-[52px] shrink-0 items-center justify-between gap-3 border-b px-3">
        <div className="flex items-center gap-2">
          <WithTip tip="Hide the document">
            <span>
              <IconButton
                materialIcon="right_panel_close"
                size="medium"
                container="medium"
                onClick={() => setIsOpen(false)}
              />
            </span>
          </WithTip>
          <span className="text-text-primary text-[14px] leading-5 font-semibold">Document</span>
        </div>
        <SegmentedButton size="small">
          {TABS.map(({ key, label }) => (
            <Segment
              key={key}
              checked={tab === key}
              onClick={() => setTab(key)}
            >
              {label}
            </Segment>
          ))}
        </SegmentedButton>
      </div>
      <div className="relative min-h-0 flex-1">
        <div className={cn('absolute inset-0', { invisible: tab !== 'pdf' })}>
          <PdfView
            documentKey={documentKey}
            isActive={tab === 'pdf'}
          />
        </div>
        {hasOpenedText && (
          <div className={cn('absolute inset-0', { invisible: tab !== 'text' })}>
            <TextView
              documentKey={documentKey}
              transcripts={transcripts}
              isActive={tab === 'text'}
            />
          </div>
        )}
      </div>
    </aside>
  )
}
